import React, { useState } from "react";

import { Link, useNavigate, useParams } from "react-router-dom";

import dayjs from "dayjs";
import {
  ArrowLeft,
  ArrowRight,
  Calendar,
  Check,
  Copy,
  FileText,
  Loader2,
  MapPin,
  Send,
  Trash2,
  User,
  X,
} from "lucide-react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "~/components/ui/alert-dialog";
import { Button } from "~/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Separator } from "~/components/ui/separator";
import { Skeleton } from "~/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import QuotationStatusBadge from "~/pages/presupuestos/components/QuotationStatusBadge";
import {
  useQuotation,
  useQuotations,
} from "~/pages/presupuestos/hooks/useQuotations";
import {
  canConvertQuotation,
  canDeleteQuotation,
  canEditQuotation,
} from "~/types/quotation";
import { formatCurrency } from "~/utils/helpers";
import { generateQuotationPdf } from "~/utils/quotationPdf";

const PresupuestoDetailPage: React.FC = () => {
  const params = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [rejectReason, setRejectReason] = useState("");
  const [isRejectDialogOpen, setIsRejectDialogOpen] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

  const { data: quotation, isLoading } = useQuotation(params.id);

  const handleGeneratePdf = async () => {
    if (!quotation) return;
    setIsGeneratingPdf(true);
    try {
      await generateQuotationPdf(quotation);
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const {
    handleMarkAsSent,
    handleMarkAsAccepted,
    handleMarkAsRejected,
    handleDelete,
    handleConvertToSalesRequest,
    handleDuplicate,
  } = useQuotations();

  if (isLoading) {
    return (
      <div className="container mx-auto space-y-4 py-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-[400px] w-full" />
      </div>
    );
  }

  if (!quotation) {
    return (
      <div className="container mx-auto py-6">
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">Presupuesto no encontrado</p>
            <Button variant="link" asChild className="mt-4">
              <Link to="/quotations">Volver a cotizaciones</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleConvert = async () => {
    const salesRequestId = await handleConvertToSalesRequest(quotation.id);
    if (salesRequestId) {
      navigate(
        `/clients/${quotation.client_id}?sales_request=${salesRequestId}`
      );
    }
  };

  const handleReject = async () => {
    await handleMarkAsRejected(quotation.id, rejectReason);
    setIsRejectDialogOpen(false);
    setRejectReason("");
  };

  const handleDeleteQuotation = async () => {
    await handleDelete(quotation.id);
    navigate("/quotations");
  };

  const handleDuplicateQuotation = async () => {
    const newId = await handleDuplicate(quotation.id);
    if (newId) {
      navigate(`/quotations/${newId}`);
    }
  };

  const isExpired = dayjs(quotation.valid_until).isBefore(dayjs());

  return (
    <div className="container mx-auto space-y-6 py-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() =>
              navigate(`/clients/${quotation.client_id}?tab=cotizaciones`)
            }
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="flex items-center gap-2 text-2xl font-bold">
              <FileText className="h-6 w-6" />
              Presupuesto #{String(quotation.quotation_number).padStart(4, "0")}
            </h1>
            <p className="text-muted-foreground">
              Creado el {dayjs(quotation.created_at).format("DD/MM/YYYY HH:mm")}
            </p>
          </div>
        </div>
        <QuotationStatusBadge status={quotation.status} className="text-sm" />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Main Content */}
        <div className="space-y-6 lg:col-span-2">
          {/* Client Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <User className="h-4 w-4" />
                Cliente
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Nombre:</span>
                <span className="font-medium">
                  {quotation.client?.name || quotation.client?.company || "-"}
                </span>
              </div>
              {quotation.client?.tax_id && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">RUT:</span>
                  <span>{quotation.client.tax_id}</span>
                </div>
              )}
              {quotation.delivery_street && (
                <div className="flex justify-between">
                  <span className="flex items-center gap-1 text-muted-foreground">
                    <MapPin className="h-3 w-3" />
                    Dirección:
                  </span>
                  <span className="text-right">
                    {quotation.delivery_street} {quotation.delivery_number}
                    {quotation.delivery_comuna &&
                      `, ${quotation.delivery_comuna}`}
                  </span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Items Table */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Productos</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Producto</TableHead>
                    <TableHead className="text-right">Cant.</TableHead>
                    <TableHead className="text-right">P. Unit.</TableHead>
                    <TableHead className="text-right">Dcto.</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {quotation.items?.map((item, index) => {
                    const lineTotal =
                      item.quantity *
                      item.unit_price *
                      (1 - (item.discount_percent || 0) / 100);
                    return (
                      <TableRow
                        key={item.id}
                        style={{
                          animationDelay: `${Math.min(index * 40, 400)}ms`,
                        }}
                        className="duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both"
                      >
                        <TableCell>
                          {item.product?.short_name ||
                            item.product?.long_name ||
                            "-"}
                        </TableCell>
                        <TableCell className="text-right">
                          {item.quantity}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(item.unit_price)}
                        </TableCell>
                        <TableCell className="text-right">
                          {item.discount_percent
                            ? `${item.discount_percent}%`
                            : "-"}
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {formatCurrency(lineTotal)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              <Separator className="my-4" />

              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Subtotal Neto:</span>
                  <span>{formatCurrency(quotation.total_amount)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>IVA (19%):</span>
                  <span>
                    {formatCurrency(
                      (quotation.price_with_iva || 0) - quotation.total_amount
                    )}
                  </span>
                </div>
                <Separator />
                <div className="flex justify-between text-lg font-bold">
                  <span>Total:</span>
                  <span>{formatCurrency(quotation.price_with_iva || 0)}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Comments */}
          {(quotation.comments || quotation.internal_notes) && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Notas</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {quotation.comments && (
                  <div>
                    <Label className="text-muted-foreground">
                      Comentarios para el cliente:
                    </Label>
                    <p className="mt-1">{quotation.comments}</p>
                  </div>
                )}
                {quotation.internal_notes && (
                  <div>
                    <Label className="text-muted-foreground">
                      Notas internas:
                    </Label>
                    <p className="mt-1 text-muted-foreground">
                      {quotation.internal_notes}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Validity Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Calendar className="h-4 w-4" />
                Vigencia
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Válido hasta:</span>
                <span className={isExpired ? "font-medium text-red-600" : ""}>
                  {dayjs(quotation.valid_until).format("DD/MM/YYYY")}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Días de vigencia:</span>
                <span>{quotation.validity_days} días</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">
                  Condición de pago:
                </span>
                <span>
                  {quotation.sales_condition?.name ||
                    `${quotation.payment_terms} días`}
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Acciones</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button
                className="w-full"
                variant="outline"
                onClick={handleGeneratePdf}
                disabled={isGeneratingPdf}
              >
                {isGeneratingPdf ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <FileText className="mr-2 h-4 w-4" />
                )}
                {isGeneratingPdf ? "Generando PDF..." : "Generar PDF"}
              </Button>

              {quotation.status === "draft" && (
                <Button
                  className="w-full"
                  onClick={() => handleMarkAsSent(quotation.id)}
                >
                  <Send className="mr-2 h-4 w-4" />
                  Marcar como Enviado
                </Button>
              )}

              {quotation.status === "sent" && (
                <>
                  <Button
                    className="w-full"
                    variant="default"
                    onClick={() => handleMarkAsAccepted(quotation.id)}
                  >
                    <Check className="mr-2 h-4 w-4" />
                    Marcar como Aceptado
                  </Button>
                  <Button
                    className="w-full"
                    variant="outline"
                    onClick={() => setIsRejectDialogOpen(true)}
                  >
                    <X className="mr-2 h-4 w-4" />
                    Marcar como Rechazado
                  </Button>
                </>
              )}

              {canConvertQuotation(quotation.status) && (
                <Button
                  className="w-full"
                  variant={
                    quotation.status === "accepted" ? "default" : "outline"
                  }
                  onClick={handleConvert}
                >
                  <ArrowRight className="mr-2 h-4 w-4" />
                  Convertir a Nota de Venta
                </Button>
              )}

              <Separator />

              <Button
                className="w-full"
                variant="outline"
                onClick={handleDuplicateQuotation}
              >
                <Copy className="mr-2 h-4 w-4" />
                Duplicar
              </Button>

              {canDeleteQuotation(quotation.status) && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button className="w-full" variant="destructive">
                      <Trash2 className="mr-2 h-4 w-4" />
                      Eliminar
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>
                        ¿Eliminar presupuesto?
                      </AlertDialogTitle>
                      <AlertDialogDescription>
                        Esta acción no se puede deshacer.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancelar</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={handleDeleteQuotation}
                        className="bg-destructive text-destructive-foreground"
                      >
                        Eliminar
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </CardContent>
          </Card>

          {/* Conversion Info */}
          {quotation.status === "converted" &&
            quotation.converted_to_sales_request_id && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Conversión</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <p className="text-sm text-muted-foreground">
                    Este presupuesto fue convertido a nota de venta.
                  </p>
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() =>
                      navigate(
                        `/clients/${quotation.client_id}?sales_request=${quotation.converted_to_sales_request_id}`
                      )
                    }
                  >
                    Ver Nota de Venta
                  </Button>
                </CardContent>
              </Card>
            )}

          {/* Rejection Info */}
          {quotation.status === "rejected" && quotation.rejection_reason && (
            <Card className="border-destructive">
              <CardHeader>
                <CardTitle className="text-lg text-destructive">
                  Motivo de Rechazo
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p>{quotation.rejection_reason}</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Reject Dialog */}
      <Dialog open={isRejectDialogOpen} onOpenChange={setIsRejectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rechazar Presupuesto</DialogTitle>
            <DialogDescription>
              Ingresa el motivo del rechazo (opcional)
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Input
              placeholder="Motivo del rechazo..."
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsRejectDialogOpen(false)}
            >
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleReject}>
              Rechazar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PresupuestoDetailPage;
